import type {
  CoreAdminProps,
  AuthProvider,
  DashboardComponent,
  LayoutComponent,
} from "ra-core";
import { CustomRoutes, localStorageStore, Resource } from "ra-core";
import { useEffect, useMemo } from "react";
import { Route } from "react-router";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { Admin } from "@/components/admin/admin";
import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";
import { OAuthConsentPage } from "@/components/supabase/oauth-consent-page";

import companies from "../companies";
import contacts from "../contacts";
import deals from "../deals";
import { SignupPage } from "../login/SignupPage";
import { ConfirmationRequired } from "../login/ConfirmationRequired";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import {
  getAuthProvider as defaultAuthProviderBuilder,
  getDataProvider as defaultDataProviderBuilder,
} from "../providers/supabase";
import sales from "../sales";
import { SettingsPageMobile } from "../settings/SettingsPageMobile";
import { ProfilePage } from "../settings/ProfilePage";
import { SettingsPage } from "../settings/SettingsPage";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import {
  defaultCompanySectors,
  defaultCurrency,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider as defaulti18nProvider } from "../providers/commons/i18nProvider";
import { StartPage } from "../login/StartPage.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { MobileTasksList } from "../tasks/MobileTasksList.tsx";
import { ContactListMobile } from "../contacts/ContactList.tsx";
import { ContactShow } from "../contacts/ContactShow.tsx";
import { NoteShowPage } from "../notes/NoteShowPage.tsx";
import { DirectoryExplorer } from "../raftel/directory/DirectoryExplorer.tsx";
import { RoutePage } from "../raftel/directory/RoutePage.tsx";
import { PrivacyNoticePage } from "../raftel/privacy/PrivacyNoticePage.tsx";
import { TermsPage } from "../raftel/privacy/TermsPage.tsx";
import { CompanyFicha, DirectoryFicha } from "../raftel/ficha/Ficha360.tsx";
import { MyDay } from "../raftel/myday/MyDay.tsx";
import { DesktopDashboard, TableroPage } from "../raftel/dashboard/Tablero.tsx";
import { RaftelLayout } from "../raftel/layout/RaftelLayout.tsx";
import { AjustesPage } from "../raftel/pages/AjustesPage.tsx";
import { TextsToValidate } from "../raftel/ai/TextsToValidate.tsx";
import { RadarPage } from "../raftel/radar/RadarPage.tsx";
import { OwnerOnly } from "../raftel/OwnerOnly.tsx";
import { GoogleConfirmPage } from "../raftel/reviews/GoogleConfirmPage.tsx";
import { InvestigacionesPage } from "../raftel/research/InvestigacionesPage.tsx";
import { CallSession } from "../raftel/session/CallSession.tsx";
import { BoardIndex, OfferBoard } from "../raftel/pipeline/OfferBoard.tsx";
import { CompanyBoard } from "../raftel/pages/CompanyBoard.tsx";
import {
  TemplateCreate,
  TemplateEdit,
  TemplateList,
} from "../raftel/templates/Templates.tsx";
import {
  SequenceCreate,
  SequenceEdit,
  SequenceList,
  SequenceStepCreate,
  SequenceStepEdit,
} from "../raftel/sequences/Sequences.tsx";
import {
  DoNotContactCreate,
  DoNotContactList,
} from "../raftel/dnc/DoNotContact.tsx";

const defaultStore = localStorageStore(undefined, "CRM");

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  i18nProvider?: CoreAdminProps["i18nProvider"];
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * seeds the store with any custom prop values for backwards compatibility.
 *
 * @param {LabeledValue[]} companySectors - The list of company sectors used in the application.
 * @param {string} currency - The ISO 4217 currency code used to format monetary values (e.g. "USD", "EUR", "GBP").
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {LabeledValue[]} dealCategories - The categories of deals used in the application.
 * @param {string[]} dealPipelineStatuses - The statuses of deals in the pipeline used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} darkModeLogo - Logo shown in dark mode and on the auth pages. Must be an imported asset, an absolute URL, or a data URI — never a route-relative path like "./logos/x.svg", which breaks on nested routes such as /oauth/consent (issue #291).
 * @param {string} lightModeLogo - Logo shown in light mode. Same rule as darkModeLogo: imported asset, absolute URL, or data URI only.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {LabeledValue[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/atomic-crm/dashboard/CRM';
 *
 * const App = () => (
 *     <CRM
 *         darkModeLogo="https://example.com/logo-dark.svg"
 *         lightModeLogo="https://example.com/logo-light.svg"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  companySectors = defaultCompanySectors,
  currency = defaultCurrency,
  dealCategories = defaultDealCategories,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProviderBuilder(),
  authProvider = defaultAuthProviderBuilder(),
  i18nProvider = defaulti18nProvider,
  store = defaultStore,
  disableTelemetry,
  ...rest
}: CRMProps) => {
  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);

  // Seed the store with CRM prop values if not already stored
  // (backwards compatibility for prop-based config)
  useEffect(() => {
    if (!store.getItem(CONFIGURATION_STORE_KEY)) {
      store.setItem(CONFIGURATION_STORE_KEY, {
        companySectors,
        currency,
        dealCategories,
        dealPipelineStatuses,
        dealStages,
        noteStatuses,
        taskTypes,
        title,
        darkModeLogo,
        lightModeLogo,
      } satisfies ConfigurationContextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const isMobile = useIsMobile();

  // on login, pre-fetch the configuration to avoid a flickering
  // when accessing the app for the first time
  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const result = await authProvider.login(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      handleCallback: async (params: any) => {
        if (!authProvider.handleCallback) {
          throw new Error(
            "handleCallback is not implemented in the authProvider",
          );
        }
        const result = await authProvider.handleCallback(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      logout: async (params: any) => {
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, store],
  );

  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  return (
    <ResponsiveAdmin
      dataProvider={dataProvider}
      authProvider={wrappedAuthProvider}
      i18nProvider={i18nProvider}
      store={store}
      loginPage={StartPage}
      requireAuth
      disableTelemetry
      {...rest}
    />
  );
};

const DesktopAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  return (
    <Admin
      layout={props.layout ?? RaftelLayout}
      dashboard={props.dashboard ?? MyDay}
      {...props}
    >
      <CustomRoutes noLayout>
        <Route path={SignupPage.path} element={<SignupPage />} />
        <Route
          path={ConfirmationRequired.path}
          element={<ConfirmationRequired />}
        />
        <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
        <Route
          path={ForgotPasswordPage.path}
          element={<ForgotPasswordPage />}
        />
        <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
        <Route path={PrivacyNoticePage.path} element={<PrivacyNoticePage />} />
        <Route path={TermsPage.path} element={<TermsPage />} />
      </CustomRoutes>

      <CustomRoutes>
        <Route path={ProfilePage.path} element={<ProfilePage />} />
        <Route path={SettingsPage.path} element={<SettingsPage />} />
        <Route path={ImportPage.path} element={<ImportPage />} />
        <Route path={ChangelogPage.path} element={<ChangelogPage />} />
        <Route path="/mi-dia" element={<MyDay />} />
        <Route path="/tablero" element={<DesktopDashboard />} />
        <Route
          path="/radar"
          element={
            <OwnerOnly>
              <RadarPage />
            </OwnerOnly>
          }
        />
        <Route
          path="/fichas-google"
          element={
            <OwnerOnly>
              <GoogleConfirmPage />
            </OwnerOnly>
          }
        />
        <Route path="/investigaciones" element={<InvestigacionesPage />} />
        <Route path="/sesion" element={<CallSession />} />
        <Route path="/ajustes" element={<AjustesPage />} />
        <Route path="/textos" element={<TextsToValidate />} />
        <Route path="/board" element={<BoardIndex />} />
        <Route path="/board/:offerId" element={<OfferBoard />} />
        <Route path="/directory/ruta" element={<RoutePage />} />
      </CustomRoutes>
      <Resource name="deals" {...deals} />
      <Resource name="contacts" {...contacts} />
      <Resource
        name="companies"
        {...companies}
        list={CompanyBoard}
        show={CompanyFicha}
      />
      <Resource name="contact_notes" />
      <Resource name="deal_notes" />
      <Resource name="tasks" list={MobileTasksList} />
      <Resource name="sales" {...sales} />
      <Resource name="tags" />
      <Resource
        name="directory"
        list={DirectoryExplorer}
        show={DirectoryFicha}
      />
      <Resource name="offers" recordRepresentation="name" />
      <Resource
        name="templates"
        list={TemplateList}
        create={TemplateCreate}
        edit={TemplateEdit}
      />
      <Resource
        name="sequences"
        list={SequenceList}
        create={SequenceCreate}
        edit={SequenceEdit}
      />
      <Resource
        name="sequence_steps"
        create={SequenceStepCreate}
        edit={SequenceStepEdit}
      />
      <Resource
        name="do_not_contact"
        list={DoNotContactList}
        create={DoNotContactCreate}
      />
    </Admin>
  );
};

const MobileAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
        networkMode: "offlineFirst",
      },
      mutations: {
        networkMode: "offlineFirst",
      },
    },
  });
  const asyncStoragePersister = createAsyncStoragePersister({
    storage: localStorage,
  });

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: asyncStoragePersister }}
    >
      <Admin
        queryClient={queryClient}
        layout={props.layout ?? RaftelLayout}
        dashboard={props.dashboard ?? MyDay}
        {...props}
      >
        <CustomRoutes noLayout>
          <Route path={SignupPage.path} element={<SignupPage />} />
          <Route
            path={ConfirmationRequired.path}
            element={<ConfirmationRequired />}
          />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route
            path={ForgotPasswordPage.path}
            element={<ForgotPasswordPage />}
          />
          <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
          <Route
            path={PrivacyNoticePage.path}
            element={<PrivacyNoticePage />}
          />
          <Route path={TermsPage.path} element={<TermsPage />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route
            path={SettingsPageMobile.path}
            element={<SettingsPageMobile />}
          />
          <Route path={ChangelogPage.path} element={<ChangelogPage />} />
          <Route path="/mi-dia" element={<MyDay />} />
          <Route path="/tablero" element={<TableroPage />} />
          <Route
            path="/radar"
            element={
              <OwnerOnly>
                <RadarPage />
              </OwnerOnly>
            }
          />
          <Route
            path="/fichas-google"
            element={
              <OwnerOnly>
                <GoogleConfirmPage />
              </OwnerOnly>
            }
          />
          <Route path="/investigaciones" element={<InvestigacionesPage />} />
          <Route path="/sesion" element={<CallSession />} />
          <Route path="/ajustes" element={<AjustesPage />} />
          <Route path="/textos" element={<TextsToValidate />} />
          <Route path="/board" element={<BoardIndex />} />
          <Route path="/board/:offerId" element={<OfferBoard />} />
          <Route path="/directory/ruta" element={<RoutePage />} />
        </CustomRoutes>
        <Resource
          name="contacts"
          list={ContactListMobile}
          show={ContactShow}
          recordRepresentation={contacts.recordRepresentation}
        >
          <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
        </Resource>
        <Resource name="companies" list={CompanyBoard} show={CompanyFicha} />
        <Resource name="tasks" list={MobileTasksList} />
        <Resource name="deals" {...deals} />
        <Resource
          name="directory"
          list={DirectoryExplorer}
          show={DirectoryFicha}
        />
        <Resource name="offers" recordRepresentation="name" />
        <Resource
          name="templates"
          list={TemplateList}
          create={TemplateCreate}
          edit={TemplateEdit}
        />
        <Resource
          name="sequences"
          list={SequenceList}
          create={SequenceCreate}
          edit={SequenceEdit}
        />
        <Resource
          name="sequence_steps"
          create={SequenceStepCreate}
          edit={SequenceStepEdit}
        />
        <Resource
          name="do_not_contact"
          list={DoNotContactList}
          create={DoNotContactCreate}
        />
      </Admin>
    </PersistQueryClientProvider>
  );
};
